import { useState } from "react";
import { useHistory } from "react-router";
import { Box, Button, Grid, Typography } from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import * as yup from "yup";

import FormikFormField from "components/shared/FormikFormField";
import PasswordToggleIcon from "components/shared/PasswordToggleIcon";
import { RESET_PASSWORD } from "helpers/apis";
import useAxios from "helpers/axios";
import { ToastTypes } from "hooks/useApp";
import { useQuery } from "hooks/useRouter";
import { IForm, passwordValidation } from "types/form";

interface IResetPasswordForm extends IForm {
  newPassword: string;
}

const validationSchema = yup.object({
  newPassword: passwordValidation,
});

const ResetPasswordForm = () => {
  const [visiblePassword, setVisiblePassword] = useState<boolean>(false);
  const query = useQuery();
  const history = useHistory();

  const { fetchData, loading } = useAxios({
    config: { url: RESET_PASSWORD, method: "POST" },
    callOnInit: false,
  });

  const onSubmit = (values: IResetPasswordForm) => {
    fetchData(
      {
        data: {
          token: query.get("token") ?? "",
          password: values.newPassword,
        },
      },
      ({
        response,
        setToastMessage,
      }: {
        response: {
          data: null;
          message: string;
        };
        setToastMessage: Function;
      }) => {
        history.push("/reset-password-success");
        setToastMessage({
          message: response.message,
          type: ToastTypes.SUCCESS,
        });
      },
      ({ error, setToastMessage }: any) => {
        let errorMessage =
          error?.toLowerCase() ||
          "An error occurred. Please try again or contact support.";
        switch (error?.toLowerCase()) {
          case "invalid token":
            errorMessage = "One-Time Password Reset Link Has Been Used";
            break;
          case "token expired":
            errorMessage =
              "Password Reset Link Expired. Please request a new link to reset your password.";
            break;
        }
        setToastMessage({ message: errorMessage, duration: null });
      }
    );
  };

  return (
    <Formik
      initialValues={{
        newPassword: "",
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {(props: FormikProps<IResetPasswordForm>) => {
        const { dirty, isValid } = props;

        return (
          <Form>
            <Box mb={3}>
              <Typography variant="h1" align="center">
                Password Reset
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormikFormField
                  name="newPassword"
                  type={visiblePassword ? "text" : "password"}
                  {...props}
                  textFieldProps={{
                    InputProps: {
                      endAdornment: (
                        <PasswordToggleIcon
                          visible={visiblePassword}
                          setVisible={setVisiblePassword}
                        />
                      ),
                    },
                    FormHelperTextProps: {
                      style: {
                        position: "static",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box mt={1}>
                  <Grid container justifyContent="center">
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={loading || !dirty || !isValid}
                      type="submit"
                      size="large"
                    >
                      Reset my password
                    </Button>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ResetPasswordForm;
